import { atom } from "jotai";
import {
    balanceSheetMetricsAtom,
    balanceSheetResolutionAtom,
} from "../../../metrics.state";
import { applyWindowToMetrics } from "../../../utils/applyWindowToMetric";
import { FinanceLabField, formatFinanceLabValue } from "@/entities/FinanceLab";
import { getMetric } from "../../../utils/getMetric";
import { getPeriodColumns, getUniquePeriods } from "../../../utils/periods";
import { filterPeriodsByWindow } from "../../../utils/filterPeriodsByWindow";
import { HeaderWithTooltip } from "../../../components/HeaderWithTooltip";
import { rowConfig } from "./rowConfig";
import { calculateDelta, getPeriodSlices } from "../../../utils/deltas";
import { ColDef } from "ag-grid-enterprise";
import { DeltaCellRenderer } from "../DeltaCellRenderer";
import { RowConfigWithMetric, SummaryRow } from "../SummaryFinancials.types";

export const BSSummaryMetricsAtom = atom<RowConfigWithMetric[]>((get) => {
    const balanceSheetMetrics = get(balanceSheetMetricsAtom);
    const metricResolution = get(balanceSheetResolutionAtom);

    const windowedMetrics = applyWindowToMetrics(
        balanceSheetMetrics,
        metricResolution.window,
    );

    return rowConfig.map((row) => {
        return {
            metric: getMetric(
                windowedMetrics,
                row.codes[0] as string,
                row.codes[1] as FinanceLabField,
            ),
            ...row,
        };
    });
});

export const BSSummaryPeriodsAtom = atom<string[]>((get) => {
    const metrics = get(BSSummaryMetricsAtom);
    const metricResolution = get(balanceSheetResolutionAtom);
    const periods = filterPeriodsByWindow(
        getUniquePeriods(
            metrics.map((m) => m.metric).filter((m) => m !== undefined),
        ),
        metricResolution.window,
    );
    return periods;
});

export interface PeriodColumn {
    field: string;
    headerName: string;
    headerComponent?: any;
    headerComponentParams?: any;
}

// Column definitions for the balance sheet summary table
export const BSSummaryColumnAtom = atom<ColDef[]>((get) => {
    const periods = get(BSSummaryPeriodsAtom);
    const periodColumns = getPeriodColumns(periods);

    return [
        {
            field: "label",
            headerName: "",
            flex: 1.5,
            pinned: "left",
            cellClass: "heading-s",
        },
        ...periodColumns,
        {
            field: "popChange",
            pinned: "right",
            maxWidth: 150,
            headerName:
                periodColumns.length >= 2
                    ? `% Δ ${periodColumns[periodColumns.length - 2].headerName} — ${periodColumns[periodColumns.length - 1].headerName}`
                    : "% Δ",
            headerComponent: HeaderWithTooltip,
            cellRenderer: DeltaCellRenderer,
            headerComponentParams: {
                tooltipText:
                    "Shows the % difference between the latest two available periods. Eg. if the last closed period is Q1-25, the delta shows the difference between Q1-25 and Q4-24.",
            },
        },
    ];
});

export const BSSummaryDataAtom = atom<SummaryRow[]>((get) => {
    const rows = get(BSSummaryMetricsAtom);
    const periods = get(BSSummaryPeriodsAtom);
    return rows.map((r) => {
        const row: SummaryRow = { ...r, previousSlice: [], currentSlice: [] };
        if (!r.metric) {
            periods.forEach((period) => {
                row[period] = "-";
            });
            row.popChange = null;
            return row;
        }

        const valuesByPeriod = new Map(
            r.metric.values.map((v) => [v.period, v.value]),
        );
        const slices = getPeriodSlices(1, Array.from(valuesByPeriod.entries()));
        const delta = calculateDelta(
            slices?.current || [],
            slices?.previous || [],
        );

        if (slices && delta !== null) {
            row.popChange = delta;
            row.previousSlice = slices.previous;
            row.currentSlice = slices.current;
        }

        valuesByPeriod.forEach((value, period) => {
            row[period] =
                value !== undefined
                    ? formatFinanceLabValue(value, row.fieldType)
                    : "-";
        });

        return row;
    });
});
